// @ts-nocheck

import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { Target } from '../../client';
import { useCSSVariablesContext } from '../../contexts/CSSVariablesContextProvider';
import { MatchRouteContext } from '../../contexts/MatchRouteContext';
import usePictureInPicture from '../../hooks/usePictureInPicture';
import { __ } from '../../i18n/l10n';
import { MatchEntityStatus } from '../../states/entities/matches';
import theme from '../../styled-components/dark';
import { AutoScrollAnchor } from '../../styled-components/shared';
import { type MatchStatus } from '../../types/match';
import { MatchViewModes, type MatchViewMode } from '../../types/matchView';
import { type SportId } from '../../types/sports';
import DocumentTitle from '../DocumentTitle';
import MatchContainer from '../Match/MatchContainer';
import NotFoundView from '../NotFoundView';
import WinaLoader from '../WinaLoader';

export const MATCH_ROUTE_ANCHOR_ID = 'match-route-anchor';

const MatchView = styled.div`
  ${!Target.__MOBILE__ ? 'font-size: 1.2em;' : ''}
  ${({ $CSSVariablesStyleSheet }) => $CSSVariablesStyleSheet}
`;

type RoutesMatchProps = {
  matchId?: number;
  loading: boolean;
  isNull: boolean;
  isOutright?: boolean;
  status?: MatchStatus;
  sportId?: SportId;
  score?: string;
  title?: string;
  roundName?: string;
  tournamentName?: string;
  sportName?: string;
  isMMLoading: boolean;
  matchViewMode?: MatchViewMode;
};

const RoutesMatch = ({
  matchId,
  loading,
  isNull,
  isOutright = false,
  status,
  sportId,
  score = '',
  title = '',
  roundName,
  tournamentName = '',
  sportName = '',
  isMMLoading,
  matchViewMode,
}: RoutesMatchProps) => {
  const matchViewElementRef = useRef();
  const { closeStream, matchId: streamMatchId } = usePictureInPicture();
  const { styleSheet, setProperties, deleteProperty } = useCSSVariablesContext();

  useEffect(() => {
    if (matchViewMode === MatchViewModes.MY_MATCH) {
      setProperties({
        '--OddButton-valueOpacity': isMMLoading ? 0 : 1,
        '--OddButton-loaderOpacity': isMMLoading ? 1 : 0,
        '--OddButton-pointerEvents': isMMLoading ? 'none' : 'auto',
        '--FavoriteButton-defaultFill': theme.FavoriteButton.mymatch.color,
        '--FavoriteButton-defaultStroke': theme.FavoriteButton.mymatch.stroke,
      });
    } else {
      deleteProperty(
        '--OddButton-valueOpacity',
        '--OddButton-loaderOpacity',
        '--OddButton-pointerEvents',
        '--FavoriteButton-defaultFill',
        '--FavoriteButton-defaultStroke',
      );
    }
  }, [setProperties, isMMLoading, matchViewMode, deleteProperty]);

  // close stream if match is null
  useEffect(() => {
    if (!loading && isNull && streamMatchId === matchId) {
      closeStream();
    }
  }, [closeStream, isNull, loading, streamMatchId, matchId]);

  const documentTitle = useMemo(() => {
    let documentTitle = __('!desc:DocumentTitle');
    if (loading === false) {
      if (!isNull && !!sportName && !!tournamentName) {
        if (isOutright === true) {
          const suffixe = `${sportName} | ${title} | `;
          documentTitle = suffixe + __('Betting with Winamax');
        } else {
          const roundSuffixe = roundName ? `${roundName} | ` : '';
          const suffixe = `${title} | ${roundSuffixe}${tournamentName} | ${sportName} | `;
          documentTitle = suffixe + __('Betting with Winamax');
          if (MatchEntityStatus.isLive(status) && [1, 2, 12].indexOf(sportId) !== -1) {
            documentTitle = `${score && score.replace(':', ' - ')} ${documentTitle}`;
          }
        }
      }
    }
    return documentTitle;
  }, [isNull, isOutright, loading, roundName, score, sportId, sportName, status, title, tournamentName]);

  const forwardedState = useMemo(() => ({ matchId, sportId }), [matchId, sportId]);

  if (loading) {
    return <WinaLoader />;
  }

  return (
    // $FlowFixMe
    <DocumentTitle title={documentTitle}>
      <MatchRouteContext.Provider value={forwardedState}>
        <MatchView ref={matchViewElementRef} className="match" $CSSVariablesStyleSheet={styleSheet}>
          <AutoScrollAnchor data-anchorid={MATCH_ROUTE_ANCHOR_ID} />
          {isNull ? <NotFoundView label={__('Match unavailable')} /> : null}
          {!isNull ? <MatchContainer /> : null}
        </MatchView>
      </MatchRouteContext.Provider>
    </DocumentTitle>
  );
};

export default RoutesMatch;
